/**
 * Закрепления клиента в карточке клиента у команды: менеджер клиента (team-access) — ведёт клиента
 * целиком, и менеджер по сопровождению (client-support-manager) — помогает в кабинете. Назначают
 * руководитель и администратор; остальные видят их без правки. Клиенту они не показываются;
 * ответственного в заказе назначают отдельно, в заказе.
 */
import { useState } from 'react'
import { teamApi, type ClientManager } from '@/lib/api'
import { useAuth } from '@/app/auth-context'
import { getErrorMessage } from '@/lib/errors'
import { Card } from '@/design-system/components/layout/card'
import { Badge } from '@/design-system/components/primitives/badge'
import { Hint } from '@/design-system/components/primitives/text'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import { canAssign } from './roles'
import { staffOptions, useStaff } from './use-staff'

/** Значение пункта «Не назначен»: у Radix Select пустая строка зарезервирована. */
const NONE = '__none__'

/** Телефон и почта одной строкой; пустые части пропускаются. */
export function managerContacts(manager: { phone: string; email: string }): string {
  return [manager.phone, manager.email].filter(Boolean).join(' · ')
}

/** Что за закрепление показывает карточка: подписи, запрос и подсказка. */
interface AssigneeKind {
  title: string
  saveError: string
  save: (cid: string, accountId: string) => Promise<ClientManager | undefined>
  hint: string
  disabledHint: string
}

const CLIENT_MANAGER: AssigneeKind = {
  title: 'Менеджер клиента',
  saveError: 'Не удалось назначить менеджера',
  save: async (cid, id) => (await teamApi.setClientManager(cid, id)).manager ?? undefined,
  hint: 'Менеджер ведёт клиента целиком; клиенту он не показывается. Ответственного по каждому заказу назначают в самом заказе.',
  disabledHint: 'Учётная запись менеджера отключена — назначьте действующего сотрудника.',
}

/**
 * Менеджер по сопровождению (client-support-manager; ТЗ 26.10.2026, п.9.1; ответ В27) — отдельная
 * от менеджера клиента роль: онбординг в кабинете, помощь с заявкой, проверка. По умолчанию — тот,
 * кто создал организацию; назначают руководитель и администратор.
 */
const SUPPORT_MANAGER: AssigneeKind = {
  title: 'Менеджер по сопровождению',
  saveError: 'Не удалось назначить менеджера по сопровождению',
  save: async (cid, id) =>
    (await teamApi.setClientSupportManager(cid, id)).supportManager ?? undefined,
  hint: 'Помогает клиенту в кабинете: с заявками и их проверкой. Видит клиента и все его заказы; клиенту не показывается.',
  disabledHint:
    'Учётная запись менеджера по сопровождению отключена — назначьте действующего сотрудника.',
}

function ClientAssigneeCard({
  kind,
  cid,
  manager,
  onChanged,
  className,
}: {
  kind: AssigneeKind
  cid: string
  manager: ClientManager | undefined
  onChanged: (next: ClientManager | undefined) => void
  className?: string
}) {
  const { context } = useAuth()
  const editable = canAssign(context?.user.role)
  const staff = useStaff(editable)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function change(value: string) {
    if (saving) return
    setError(null)
    setSaving(true)
    try {
      onChanged(await kind.save(cid, value === NONE ? '' : value))
    } catch (err) {
      setError(getErrorMessage(err, kind.saveError))
    } finally {
      setSaving(false)
    }
  }

  const options = staffOptions(staff, manager)

  return (
    <Card header={kind.title} className={className}>
      {error ? (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {editable ? (
          <div className="w-full max-w-narrow">
            <Select
              value={manager?.id ?? NONE}
              onValueChange={(v) => void change(v)}
              disabled={saving}
            >
              <SelectTrigger aria-label={kind.title}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Не назначен</SelectItem>
                {options.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <span className="font-medium text-txt">{manager?.fullName ?? 'Не назначен'}</span>
        )}
        {manager ? (
          <span className="text-13 text-txt-secondary">{managerContacts(manager)}</span>
        ) : null}
        {manager?.disabled ? <Badge variant="gray">Учётная запись отключена</Badge> : null}
      </div>
      <Hint className="mt-2">{manager?.disabled ? kind.disabledHint : kind.hint}</Hint>
    </Card>
  )
}

export function ClientManagerCard(props: {
  cid: string
  manager: ClientManager | undefined
  onChanged: (next: ClientManager | undefined) => void
  className?: string
}) {
  return <ClientAssigneeCard kind={CLIENT_MANAGER} className="mb-4" {...props} />
}

/** Менеджер по сопровождению в карточке клиента у команды (client-support-manager). */
export function ClientSupportManagerCard(props: {
  cid: string
  manager: ClientManager | undefined
  onChanged: (next: ClientManager | undefined) => void
  className?: string
}) {
  return <ClientAssigneeCard kind={SUPPORT_MANAGER} className="mb-4" {...props} />
}
